import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/react";
import { Badge, Button, Card, Col, Drawer, Flex, Input, type InputRef, Row, Switch, Typography, message } from "antd";
import TextArea, { type TextAreaRef } from "antd/es/input/TextArea";
import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { isFromGWUL, isFromLLT } from "../../domain/groups.domain";
import { NoteType } from "../../domain/notes.domain";
import { PRACTICE_AREA, isLastPlusOneNote } from "../../domain/user.domain";
import { useRootPath } from "../../hooks/app.hook";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import { createClient, validateClientName } from "../../service/client.service";
import {
  completeFileUpload,
  handleCancelRecording,
  handleWriteNoteSubmission,
  handleWriteNoteSubmissionWithContext,
  initiateFileUpload,
  uploadFileToGCS,
  uploadPDFsAndCreateNote,
  uploadSessionContextPdfs,
} from "../../service/notes.service";
import { resumableUploadManager } from "../../service/upload.service";
import {
  setAddClientView,
  setAudioDevices,
  setCreateModalState,
  setCustomiseSectionState,
  setDemoState,
  setNotesViewVisible,
  setSelectedClient,
  setSelectedView,
  setShowDemoSignupModal,
  setShowRecordingView,
  setShowRestrictionModal,
  setUploadType,
} from "../../slices/appStateSlice";
import { addClient, resetClientForm } from "../../slices/clientSlice";
import {
  setIsPaused,
  setIsRecording,
  setMediaRecorder,
  setRecordMimeType,
  setRecordingDetail,
  setRecordingDevice,
  setWithHeadphones,
} from "../../slices/recordingsSlice";
import { setShowPaymentModal } from "../../slices/userSlice";
import type { TRecordingDetailsType } from "../../types/index.type";
import { EventType, getSystemMetadata, trackEvent } from "../../utils/analytics";
import { getChunkStats, saveChunk } from "../../utils/audioStore";
import {
  logAudioPipelineError,
  logAudioStoreSaveFailure,
  logGcsUploadFailure,
  logStorageQuotaExhausted,
} from "../../utils/audioStoreLogger";
import { isReclaimableStorageError, recoverFromStorageQuota } from "../../utils/audioStoreQuota";
import {
  LC_KEYS,
  RECORDER_AUDIO_BITRATE_BPS_GCS,
  RECORDER_TIMESLICE_MS_GCS,
  USE_INDEXEDDB_BACKUP,
  VALIDATION_TIME_SECONDS,
} from "../../utils/constants";
import { showPaymentModal } from "../../utils/datamanipulation.utils";
import { isIOS } from "../../utils/device.utils";
import { eventEmitter } from "../../utils/eventEmitter";
import {
  type DeviceChangeConfig,
  type MediaRecorderConfig,
  type StartRecordingConfig,
  createCleanupAudioResources,
  createDeviceChangeHandler,
  fetchAudioInputDevices,
  handleDeviceChange,
  initiateMediaRecorder as initMediaRecorder,
  registerMicStream,
  handleStartRecording as startRecording,
  stopAllMicTracks,
} from "../../utils/mediaRecorder.utils";
import { determineBestAudioFormat } from "../../utils/recording.utils";
import SelectableBadge from "../Badge/SelectableBadge.component";
import ClientForm from "../CreateClient/ClientForm.component";
import { ViewType } from "./AITemplateModal/types";
import CPTCodeSelector from "./CPTCodeSelector.component";
import ClientSelector from "./ClientSelector.component";
import EncounterPatientBar from "./EncounterPatientBar.component";
import FileUploader from "./FileUploader.component";
import LocationSelector from "./LocationSelector.component";
import NoteTypeSelector from "./NoteTypeSelector.component";
import PDFContextUploader from "./PDFContextUploader.component";
import RowHeader from "./RowHeader.component";
import {
  AUDIO_CONTEXT_RESUME_TIMEOUT_MS,
  DEFAULT_NOTE_TYPES,
  GWUL_NOTE_TYPES,
  IOS_RECOVERY_RETRY_DELAYS_MS,
  LLT_NOTE_TYPES,
  MEDIA_RECORDER_STOP_SETTLE_MS,
  MIC_TRACKS_RELEASE_DELAY_MS,
} from "./constants";
import styles from "./createnotemodal.module.scss";

const NUDGE_NOTE_TYPES = new Set(Object.values(NoteType).filter((v) => typeof v === "number") as number[]);

/** Returns true when the selected note type uses a custom template (user-created or gallery), not a Nudge prebuilt. */
const isCustomTemplate = (recordingDetail: { is_template?: boolean; note_type?: number }) =>
  recordingDetail.is_template ||
  (recordingDetail.note_type !== undefined && !NUDGE_NOTE_TYPES.has(recordingDetail.note_type));

type CreateNoteModalGCSProps = {
  captureRecordingIdRef: React.MutableRefObject<any>;
  isCaptureEnabled: boolean;
  resetRecordingState: (withRefresh?: boolean) => void;
  handleCancel: () => Promise<void>;
  displayRecordingTimer: () => void;
  setRecordingTime: (time: number) => void;
  checkMicrophoneMuteStatus: (stream: MediaStream) => Promise<unknown>;
  getCaptureDetails: () => object;
  recordingTime: number;
};
enum ModalityType {
  LIVE_SESSION = "audio",
  DICTATE = "dictate",
  UPLOAD = "upload",
  WRITE = "write",
  NONE = "none",
}

const CreateNoteModal = React.forwardRef(
  (
    {
      captureRecordingIdRef,
      isCaptureEnabled,
      resetRecordingState,
      handleCancel,
      displayRecordingTimer,
      setRecordingTime,
      checkMicrophoneMuteStatus,
      getCaptureDetails,
      recordingTime,
    }: CreateNoteModalGCSProps,
    ref: React.Ref<any>,
  ) => {
    const componentName = "createnotemodal";
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isMobile = useIsMobileView();
    const { referIndividualBy } = useSpecialityReference();
    const titleRef = React.useRef<InputRef>(null);
    const notesRef = React.useRef<TextAreaRef>(null);
    const audioFileRef = React.useRef<File | null>(null);
    const hasRunValidationRef = React.useRef<boolean>(false);
    const userInfo = useAppSelector((state) => state.auth.userInfo);
    const {
      isEditNote,
      isDemoPath,
      isCreateModalVisible,
      selectedNoteForEditing,
      demoState,
      addClientView,
      isMobilePath,
      templates,
    } = useAppSelector((state) => state.appState);
    const { clientForm } = useAppSelector((state) => state.client);
    const clientsById = useAppSelector((state) => state.client.byId);
    const { isRecording, isPaused, mediaRecorder, recordingDetail, recordingDevice, withHeadphones, recordMimeType } =
      useAppSelector((state) => state.recordings);
    const { rootPath } = useRootPath();
    const [startingSession, setStartingSession] = React.useState(false);
    const [stream, setStream] = React.useState<MediaStreamTrack | null>(null);
    const [retryCount, setRetryCount] = React.useState<number>(0);
    const [updatingNote, setUpdatingNote] = React.useState(false);
    const [pendingPdfFiles, setPendingPdfFiles] = React.useState<File[]>([]);
    const [, setCurrentUploadTab] = React.useState<"audio" | "pdf">("audio");
    const [contextPdfFiles, setContextPdfFiles] = React.useState<File[]>([]);
    const audioContextRef = React.useRef<AudioContext | null>(null);
    const destinationRef = React.useRef<MediaStreamAudioDestinationNode | null>(null);
    const currentAudioSourceRef = React.useRef<MediaStreamAudioSourceNode | null>(null);
    const displayAudioSourceRef = React.useRef<MediaStreamAudioSourceNode | null>(null);
    const recordingTimeRef = React.useRef<number>(0); // Track current recording time to avoid closure issues
    const pendingDataHandlersRef = React.useRef<Set<Promise<void>>>(new Set());

    // Keep recordingTimeRef in sync with recordingTime prop
    React.useEffect(() => {
      recordingTimeRef.current = recordingTime;
    }, [recordingTime]);

    // Cleanup function for audio resources using shared utility
    const cleanupAudioResources = React.useCallback(
      createCleanupAudioResources({ audioContextRef, destinationRef, currentAudioSourceRef, displayAudioSourceRef }),
      [],
    );

    // Cleanup function for file uploads (audio and PDFs)
    const clearFileUploads = React.useCallback(() => {
      audioFileRef.current = null;
      setContextPdfFiles([]);
    }, []);

    // Lightweight mic-track killer — safe to call before final chunk processing
    const stopMicTracks = React.useCallback(() => {
      stopAllMicTracks({ audioContextRef, destinationRef, currentAudioSourceRef, displayAudioSourceRef });
    }, []);

    const awaitPendingDataHandlers = React.useCallback(async () => {
      while (pendingDataHandlersRef.current.size > 0) {
        const pendingHandlers = Array.from(pendingDataHandlersRef.current);
        await Promise.allSettled(pendingHandlers);
      }
    }, []);

    // Expose cleanup functions to parent component
    React.useImperativeHandle(
      ref,
      () => ({
        cleanupAudioResources,
        stopMicTracks,
        clearFileUploads,
        awaitPendingDataHandlers,
        resumeRecording: async () => {
          console.log("[RESUME] resumeRecording called via ref");
          await handleResumeRecording();
        },
      }),
      [cleanupAudioResources, stopMicTracks, clearFileUploads, awaitPendingDataHandlers],
    );

    // Cleanup audio resources when component unmounts or recording stops
    useEffect(() => {
      return () => {
        stopMicTracks();
        cleanupAudioResources();
      };
    }, [cleanupAudioResources, stopMicTracks]);

    const MODALITY_LIST = useMemo(
      () => [
        {
          title: "Live Session",
          icon: (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M1 4.4974V9.4974M4 2.83073V11.1641M7 1.16406V12.8307M10 2.83073V11.1641M13 4.4974V9.4974"
                stroke="#6B7280"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ),
          type: ModalityType.LIVE_SESSION,
        },
        {
          title: "Dictate",
          icon: (
            <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M5 12.5C6.06087 12.5 7.07828 12.0786 7.82843 11.3284C8.57857 10.5783 9 9.56087 9 8.5V7.5M5 12.5C3.93913 12.5 2.92172 12.0786 2.17157 11.3284C1.42143 10.5783 1 9.56087 1 8.5V7.5M5 12.5V15M2.5 15H7.5M5 10.5C4.46957 10.5 3.96086 10.2893 3.58579 9.91421C3.21071 9.53914 3 9.03043 3 8.5V3C3 2.46957 3.21071 1.96086 3.58579 1.58579C3.96086 1.21071 4.46957 1 5 1C5.53043 1 6.03914 1.21071 6.41421 1.58579C6.78929 1.96086 7 2.46957 7 3V8.5C7 9.03043 6.78929 9.53914 6.41421 9.91421C6.03914 10.2893 5.53043 10.5 5 10.5Z"
                stroke="#6B7280"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ),
          type: ModalityType.DICTATE,
        },
        {
          title: "Upload",
          icon: (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M1 10V11.5C1 11.8978 1.15804 12.2794 1.43934 12.5607C1.72064 12.842 2.10218 13 2.5 13H11.5C11.8978 13 12.2794 12.842 12.5607 12.5607C12.842 12.2794 13 11.8978 13 11.5V10M4 4L7 1M7 1L10 4M7 1V10"
                stroke="#6B7280"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ),
          type: ModalityType.UPLOAD,
        },
        {
          title: "Describe",
          icon: (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M8.84031 2.4008L3.36697 8.19413C3.16031 8.41413 2.96031 8.84746 2.92031 9.14746L2.67364 11.3075C2.58697 12.0875 3.14697 12.6208 3.92031 12.4875L6.06698 12.1208C6.36698 12.0675 6.78697 11.8475 6.99364 11.6208L12.467 5.82746C13.4136 4.82746 13.8403 3.68746 12.367 2.29413C10.9003 0.914129 9.78698 1.4008 8.84031 2.4008Z"
                stroke="#6B7280"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.92667 3.36719C8.06643 4.26109 8.49907 5.08328 9.15668 5.70469C9.81428 6.32609 10.6596 6.71154 11.56 6.80052M2 14.6672H14"
                stroke="#6B7280"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ),
          type: ModalityType.WRITE,
        },
        {
          title: `${referIndividualBy} History`,
          type: ModalityType.NONE,
          icon: (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10.9881 11.4834C10.5223 10.8666 9.91959 10.3665 9.22755 10.0223C8.5355 9.67812 7.77299 9.49935 7.00009 9.50005C6.22719 9.49935 5.46468 9.67812 4.77264 10.0223C4.08059 10.3665 3.47792 10.8666 3.01209 11.4834M10.9881 11.4834C11.8971 10.6748 12.5382 9.60908 12.8275 8.42744C13.1169 7.2458 13.0403 6.00411 12.6077 4.86703C12.1752 3.72996 11.4072 2.75123 10.4057 2.06065C9.40414 1.37006 8.21632 1.00024 6.99976 1.00024C5.7832 1.00024 4.59538 1.37006 3.59383 2.06065C2.59227 2.75123 1.82431 3.72996 1.39178 4.86703C0.95925 6.00411 0.882591 7.2458 1.17197 8.42744C1.46135 9.60908 2.10309 10.6748 3.01209 11.4834M10.9881 11.4834C9.89077 12.4622 8.47051 13.0021 7.00009 13C5.52944 13.0023 4.10958 12.4623 3.01209 11.4834M9.00009 5.50005C9.00009 6.03048 8.78938 6.53919 8.4143 6.91426C8.03923 7.28933 7.53052 7.50005 7.00009 7.50005C6.46966 7.50005 5.96095 7.28933 5.58588 6.91426C5.2108 6.53919 5.00009 6.03048 5.00009 5.50005C5.00009 4.96961 5.2108 4.46091 5.58588 4.08583C5.96095 3.71076 6.46966 3.50005 7.00009 3.50005C7.53052 3.50005 8.03923 3.71076 8.4143 4.08583C8.78938 4.46091 9.00009 4.96961 9.00009 5.50005Z"
                stroke="#6B7280"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          ),
        },
      ],
      [referIndividualBy],
    );

    // Run 15-second validation check based on actual recording time (not wall clock)
    // This respects pauses - only validates after VALIDATION_TIME_SECONDS seconds of actual recording
    useEffect(() => {
      if (isRecording && recordingTime === VALIDATION_TIME_SECONDS && !hasRunValidationRef.current) {
        console.log(`[VALIDATION] Recording time reached ${VALIDATION_TIME_SECONDS}, running validation`);
        hasRunValidationRef.current = true;
        validateRecordingDataCapture();
      }

      // Reset validation flag when recording stops/resets
      if (!isRecording && recordingTime === 0) {
        hasRunValidationRef.current = false;
      }
    }, [isRecording, recordingTime]);

    // On a StorageQuotaError (local Cache + IndexedDB are full), reclaim space by
    // clearing the local backup of backend-confirmed done-processing recordings
    // (never the one being captured), then retry THIS chunk once. Silent — never
    // surfaces an error to the user; the GCS upload remains the primary path.
    const handleStorageQuota = React.useCallback(
      async (originalError: unknown, audioId: string, blob: Blob, eventSize: number) => {
        try {
          const result = await recoverFromStorageQuota(audioId);
          if (result.cleared.length > 0) {
            try {
              await saveChunk(audioId, blob, determineBestAudioFormat());
              return;
            } catch (retryError) {
              logAudioStoreSaveFailure(retryError, {
                audio_id: audioId,
                event_size: eventSize,
                quota_recovery: "retry_failed",
                cleared_count: result.cleared.length,
              });
              return;
            }
          }
          // Nothing safe to clear, or the sweep was suppressed by the cooldown.
          // This recurs every chunk for the rest of a wedged session, so capture
          // it ONCE per session (breadcrumb thereafter) instead of per chunk —
          // otherwise a multi-hour stuck recording floods Sentry.
          logStorageQuotaExhausted({
            audio_id: audioId,
            event_size: eventSize,
            quota_recovery: result.ranSweep ? "nothing_to_clear" : "suppressed",
          });
        } catch (recoveryError) {
          logAudioStoreSaveFailure(originalError, {
            audio_id: audioId,
            event_size: eventSize,
            quota_recovery: "recovery_failed",
            recovery_error: recoveryError instanceof Error ? recoveryError.message : String(recoveryError),
          });
        }
      },
      [],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: handler only when the underlying device changes
    useEffect(() => {
      const handleDataAvailable = (event: BlobEvent) => {
        const handlerPromise = (async () => {
          const eventSize = event.data?.size || 0;
          const isEmptyBuffer = eventSize === 0;
          const currentRecordingTime = recordingTimeRef.current; // Use ref to get current value

          if (event.data && event.data.size > 0) {
            // Persist the local backup INDEPENDENT of the GCS session — every
            // non-empty chunk is written whenever we have a recording id, even if
            // the resumable session is not (yet / no longer) active. Guarantees the
            // bytes are saved locally; recoverable via the "retry upload from
            // IndexedDB" flow.
            if (captureRecordingIdRef.current) {
              try {
                await saveChunk(captureRecordingIdRef.current.toString(), event.data, determineBestAudioFormat());
              } catch (error) {
                if (isReclaimableStorageError(error)) {
                  void handleStorageQuota(error, captureRecordingIdRef.current.toString(), event.data, eventSize);
                } else {
                  logAudioStoreSaveFailure(error, {
                    audio_id: captureRecordingIdRef.current,
                    event_size: eventSize,
                  });
                }
              }
            }

            if (resumableUploadManager.hasActiveSession()) {
              try {
                await resumableUploadManager.uploadChunk(event.data, currentRecordingTime);
              } catch (error) {
                console.warn("GCS upload failed, continuing with local backup:", error);
                // Bounded per session: a down GCS would fail every ~10s chunk.
                logGcsUploadFailure(error, { audio_id: resumableUploadManager.getAudioId() });
              }
            } else {
              console.warn("No active resumable upload session; chunk persisted to local backup only.");
            }
          } else if (isEmptyBuffer) {
            console.warn(`[AUDIO_BUFFER] Empty data event received - size: ${eventSize}`);
          }
        })().catch((error) => {
          // Bounded per session: the handler runs on every ~10s chunk.
          logAudioPipelineError(error, {
            context: "dataavailable_handler",
            audio_id: captureRecordingIdRef.current,
          });
        });

        pendingDataHandlersRef.current.add(handlerPromise);
        handlerPromise.finally(() => {
          pendingDataHandlersRef.current.delete(handlerPromise);
        });
      };

      const handleError = (event: any) => {
        console.error(`[MEDIA_RECORDER] Recording error:`, event);
        const errorMessage = event.error?.message || event.error || "Unknown MediaRecorder error";
        // Bounded per session: a recorder stuck emitting error events would flood.
        logAudioPipelineError(new Error(`MediaRecorder error: ${errorMessage}`), {
          context: "mediarecorder_error",
          audio_id: captureRecordingIdRef.current,
          media_recorder_state: mediaRecorder?.state,
          stream_active: mediaRecorder?.stream?.active,
        });
      };

      if (mediaRecorder) {
        mediaRecorder.addEventListener("dataavailable", handleDataAvailable);
        mediaRecorder.addEventListener("error", handleError);
      }

      return () => {
        if (mediaRecorder) {
          mediaRecorder.removeEventListener("dataavailable", handleDataAvailable);
          mediaRecorder.removeEventListener("error", handleError);
        }
      };
    }, [mediaRecorder]);

    useEffect(() => {
      if (isEditNote) {
        return;
      }
      const keysToUpdate = [LC_KEYS.CPT_CODE, LC_KEYS.NOTE_TYPE, LC_KEYS.LOCATION_TYPE];
      const updatedDetails: Partial<TRecordingDetailsType> = {
        ...recordingDetail,
      };
      const isTemplate = localStorage.getItem(LC_KEYS.IS_TEMPLATE) === "true";

      if ((isTemplate && templates.length === 0) || !isCreateModalVisible) {
        return;
      }

      keysToUpdate.forEach((key) => {
        const value = localStorage.getItem(key);
        if (!value) return;

        if (key === LC_KEYS.CPT_CODE) {
          updatedDetails.duration_type = value === "undefined" ? null : Number(value);
        } else if (key === LC_KEYS.NOTE_TYPE) {
          if (isTemplate) {
            //TEMP: to remove the existing template storage.
            localStorage.removeItem(LC_KEYS.NOTE_TYPE);
            localStorage.removeItem(LC_KEYS.IS_TEMPLATE);
          } else {
            updatedDetails.note_type = Number(value);
          }
        } else {
          updatedDetails.location_type = Number(value);
        }
      });

      dispatch(setRecordingDetail(updatedDetails));
    }, [isCreateModalVisible, templates, isEditNote]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: handler only when the underlying device changes
    useEffect(() => {
      if (isRecording || isPaused) {
        console.log("fire from useEffect - device change detected");
        // Don't stop the MediaRecorder, just switch the audio source
        handleNewDevice();
      }
    }, [recordingDevice?.deviceId, recordingDevice?.groupId]);

    useEffect(() => {
      const handleInactive = createDeviceChangeHandler(
        withHeadphones,
        isRecording,
        isPaused,
        recordingDevice,
        mediaRecorder,
        dispatch,
        setAudioDevices,
        setRecordingDevice,
      );

      navigator.mediaDevices?.addEventListener("devicechange", handleInactive);
      return () => {
        navigator.mediaDevices?.removeEventListener("devicechange", handleInactive);
      };
    }, [isRecording, recordingDevice, mediaRecorder, withHeadphones, dispatch, isPaused]);

    useEffect(() => {
      async function handleTrackEnded() {
        console.log("Track ended init");
        const devices = await fetchAudioInputDevices();
        dispatch(setAudioDevices(devices));
        dispatch(setRecordingDevice(devices[0]));
      }

      stream?.addEventListener("ended", handleTrackEnded);
      return () => {
        stream?.removeEventListener("ended", handleTrackEnded);
      };
    }, [dispatch, stream]);

    // iOS phone call recovery: audio source hot-swap on visibilitychange.
    // After a phone call, iOS Safari's AudioContext / source track is permanently broken.
    // Instead of stopping the MediaRecorder (which closes the WebM container and causes
    // a second concatenated container that the transcription service ignores), we rebuild
    // only the audio graph (AudioContext → source → destination) and hot-swap the track
    // on the MediaRecorder's recording stream so it keeps producing a single WebM file.
    // biome-ignore lint/correctness/useExhaustiveDependencies: refs are stable, recovery depends on recording state + mediaRecorder
    useEffect(() => {
      if (!isIOS() || (!isRecording && !isPaused)) return;

      let recoveryInProgress = false;

      const handleVisibilityRecovery = async () => {
        if (document.visibilityState !== "visible") return;
        if (recoveryInProgress) return;

        const audioCtx = audioContextRef.current;
        if (!audioCtx || audioCtx.state === "running") return;

        console.log(`[iOS_RECOVERY] AudioContext state is "${audioCtx.state}", attempting resume first`);

        // Try a simple resume first — handles tab switches without a phone call
        try {
          await Promise.race([
            audioCtx.resume(),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error("resume timeout")), AUDIO_CONTEXT_RESUME_TIMEOUT_MS),
            ),
          ]);
        } catch {
          console.log("[iOS_RECOVERY] AudioContext resume timed out or failed");
        }

        // After resume attempt, check if the source track is still healthy
        const sourceNode = currentAudioSourceRef.current as any;
        const sourceStream: MediaStream | undefined = sourceNode?.mediaStream;
        const sourceTrack: MediaStreamTrack | undefined = sourceStream?.getAudioTracks()?.[0];

        // Re-read state after async resume (TS narrowing doesn't track mutations)
        const stateAfterResume = audioCtx.state as AudioContextState;
        if (stateAfterResume === "running" && sourceTrack?.readyState === "live" && !sourceTrack.muted) {
          console.log("[iOS_RECOVERY] Simple resume succeeded, no full recovery needed");
          return;
        }

        // Full audio graph rebuild needed — but we keep the MediaRecorder alive so
        // the WebM container stays continuous (no concatenated headers).
        recoveryInProgress = true;
        console.log("[iOS_RECOVERY] Audio source hot-swap started (keeping MediaRecorder alive)");

        try {
          // Release old mic hardware so iOS can hand us a fresh stream.
          // NOTE: This will trigger the devicechange handler which may independently
          // acquire a new stream and fix the audio. We check for that below.
          stopMicTracks();
          await new Promise((resolve) => setTimeout(resolve, MIC_TRACKS_RELEASE_DELAY_MS));

          if (!captureRecordingIdRef.current) {
            console.log("[iOS_RECOVERY] Recording cancelled during wait, aborting");
            return;
          }

          // After stopMicTracks + wait, the devicechange handler may have already
          // recovered the audio (it runs concurrently). Check if AudioContext is now
          // healthy — if so, the device change handler fixed it and we can skip.
          const ctxAfterWait = audioContextRef.current;
          if (ctxAfterWait?.state === "running") {
            const srcAfterWait = currentAudioSourceRef.current as any;
            const srcTrackAfterWait: MediaStreamTrack | undefined = srcAfterWait?.mediaStream?.getAudioTracks()?.[0];
            if (srcTrackAfterWait?.readyState === "live" && !srcTrackAfterWait.muted) {
              console.log("[iOS_RECOVERY] Device change handler already recovered audio, skipping hot-swap");
              return;
            }
          }

          // Retry acquiring a new mic stream and audio graph with increasing delays.
          const retryDelays = IOS_RECOVERY_RETRY_DELAYS_MS;
          let swapSucceeded = false;

          for (let attempt = 0; attempt < retryDelays.length; attempt++) {
            if (retryDelays[attempt] > 0) {
              console.log(
                `[iOS_RECOVERY] Waiting ${retryDelays[attempt]}ms before retry ${attempt + 1}/${retryDelays.length}`,
              );
              await new Promise((resolve) => setTimeout(resolve, retryDelays[attempt]));
            }

            if (!captureRecordingIdRef.current) {
              console.log("[iOS_RECOVERY] Recording cancelled during retry wait, aborting");
              return;
            }

            console.log(`[iOS_RECOVERY] Audio source swap attempt ${attempt + 1}/${retryDelays.length}`);
            let newAudioCtx: AudioContext | null = null;
            try {
              // 1. Build a fresh audio graph: AudioContext → source → destination
              newAudioCtx = new AudioContext();
              const newDestination = newAudioCtx.createMediaStreamDestination();

              // Use plain { audio: true } — iOS rejects { exact: deviceId } constraints
              // after a phone call because the audio session hasn't fully reset.
              const newMicStream = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: false },
                video: false,
              });
              registerMicStream(newMicStream);

              const newSource = newAudioCtx.createMediaStreamSource(newMicStream);
              newSource.connect(newDestination);

              // 2. Hot-swap the track on the MediaRecorder's recording stream.
              //    The MediaRecorder was created with destination.stream — we replace
              //    the old (dead) audio track with the new (live) one so the recorder
              //    keeps writing into the same WebM container.
              const recorderStream = mediaRecorder?.stream;
              if (recorderStream) {
                const oldTracks = recorderStream.getAudioTracks();
                const newTrack = newDestination.stream.getAudioTracks()[0];

                if (newTrack) {
                  // Remove all old (broken) tracks
                  for (const t of oldTracks) {
                    recorderStream.removeTrack(t);
                  }
                  // Add the fresh track
                  recorderStream.addTrack(newTrack);
                  console.log("[iOS_RECOVERY] Track hot-swapped on MediaRecorder stream");
                }
              }

              // 3. Close the old broken AudioContext and update refs
              if (audioContextRef.current && audioContextRef.current.state !== "closed") {
                audioContextRef.current.close().catch(() => {});
              }
              audioContextRef.current = newAudioCtx;
              destinationRef.current = newDestination;
              currentAudioSourceRef.current = newSource;

              // 4. Verify the new audio graph is actually producing non-silent data.
              const verifyAudioFlowing = (): Promise<boolean> =>
                new Promise((resolve) => {
                  try {
                    const analyser = newAudioCtx!.createAnalyser();
                    analyser.fftSize = 256;
                    newSource.connect(analyser);
                    const buf = new Uint8Array(analyser.frequencyBinCount);

                    // Check a few times over 500ms
                    let checks = 0;
                    const iv = setInterval(() => {
                      analyser.getByteFrequencyData(buf);
                      const hasAudio = buf.some((v) => v > 0);
                      checks++;
                      if (hasAudio) {
                        clearInterval(iv);
                        newSource.disconnect(analyser);
                        resolve(true);
                      } else if (checks >= 5) {
                        clearInterval(iv);
                        newSource.disconnect(analyser);
                        resolve(false);
                      }
                    }, 100);
                  } catch {
                    resolve(false);
                  }
                });

              const audioFlowing = await verifyAudioFlowing();
              console.log(`[iOS_RECOVERY] Post-swap audio verification: ${audioFlowing ? "PASS" : "FAIL (silent)"}`);

              // 5. If the MediaRecorder was paused (user clicked pause before the
              //    phone call), resume it so audio flows again.
              if (mediaRecorder && mediaRecorder.state === "paused") {
                mediaRecorder.resume();
                console.log("[iOS_RECOVERY] Resumed paused MediaRecorder after hot-swap");
              }

              // Update the stream track reference for visualizer / VAD
              const newAudioTrack = newMicStream.getAudioTracks()[0];
              if (newAudioTrack) {
                setStream(newAudioTrack);
              }

              swapSucceeded = true;
              console.log(`[iOS_RECOVERY] Audio source hot-swap succeeded on attempt ${attempt + 1}`);

              if (!audioFlowing) {
                // The new source is silent — likely a hardware issue.
                // Fall back to full pipeline recreation as a last resort.
                // This creates a new MediaRecorder (concatenated WebM) but at least audio works.
                console.warn("[iOS_RECOVERY] Hot-swap produced silent audio, falling back to full pipeline recreation");
                try {
                  if (mediaRecorder && mediaRecorder.state !== "inactive") {
                    mediaRecorder.stop();
                    await new Promise((resolve) => setTimeout(resolve, MEDIA_RECORDER_STOP_SETTLE_MS));
                  }
                  const fallbackRecorder = await initiateMediaRecorder(withHeadphones, true);
                  if (fallbackRecorder) {
                    fallbackRecorder.start(RECORDER_TIMESLICE_MS_GCS);
                    console.log("[iOS_RECOVERY] Fallback pipeline recreation succeeded");
                  }
                } catch (fallbackError) {
                  console.error("[iOS_RECOVERY] Fallback pipeline recreation also failed:", fallbackError);
                }
              }
              break;
            } catch (error) {
              // Close the AudioContext we just created to avoid exhausting iOS Safari's
              // limit of ~4-6 concurrent AudioContexts.
              newAudioCtx?.close().catch(() => {});
              console.warn(`[iOS_RECOVERY] Attempt ${attempt + 1} failed:`, error);
              // Don't call stopMicTracks() between retries — the devicechange handler
              // may have acquired a working stream that we'd kill. Just wait and retry.
            }
          }

          if (!swapSucceeded) {
            console.error("[iOS_RECOVERY] All audio source swap attempts failed");
            Swal.fire({
              icon: "warning",
              title: "Microphone Unavailable",
              text: "We couldn't restart the microphone after your phone call. You can submit the session recorded so far.",
            });
          }
        } catch (error) {
          console.error("[iOS_RECOVERY] Audio source hot-swap failed:", error);
        } finally {
          recoveryInProgress = false;
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityRecovery);
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityRecovery);
      };
    }, [isRecording, isPaused, mediaRecorder, withHeadphones]);

    const handleOpenPaymentModal = () => {
      dispatch(setShowPaymentModal(true));
    };

    const shouldShowHeadphoneSwitch =
      !isEditNote && !isMobile && recordingDetail.modality_type === ModalityType.LIVE_SESSION;

    const validateFields = () => {
      const { modality_type, note_type, description } = recordingDetail;
      const notesDescription = description ? description : notesRef.current?.resizableTextArea?.textArea.value || "";

      if (note_type === undefined || note_type === null) {
        message.error("Please select a note type");
        return false;
      }

      if (modality_type === "upload") {
        const hasPDFs = pendingPdfFiles.length > 0;
        const hasAudio = audioFileRef.current !== null;

        if (!hasPDFs && !hasAudio) {
          message.error("Please upload an audio or PDF file");
          return false;
        }
      }

      if (modality_type === "write" && notesDescription.length < 20) {
        message.error("Description should be at least 20 characters");
        return false;
      }

      return true;
    };

    const handleNoteUpdate = async () => {
      if (checkDemoPath()) {
        return;
      }
      if (showPaymentModal(userInfo)) {
        trackEvent(EventType.FREE_TRIAL_LIMIT_MODAL);
        handleOpenPaymentModal();
        dispatch(setCreateModalState(false));
        return;
      }
      // Without the note being edited there is nothing to regenerate FROM —
      // submitting anyway would silently mint an unrelated blank note. Say so
      // instead of doing nothing.
      if (!selectedNoteForEditing?.id) {
        message.error("We couldn't find the note to regenerate. Please reopen it and try again.");
        return;
      }
      const value = titleRef.current?.input?.value || "Untitled Session";
      const clientUuid =
        recordingDetail.client_uuid ??
        (recordingDetail.client_id != null ? clientsById[recordingDetail.client_id]?.uuid : undefined);

      setUpdatingNote(true);
      try {
        if (recordingDetail.note_type === undefined) {
          message.error("Please select a note type");
          setUpdatingNote(false);
          return;
        }
        const response = await handleWriteNoteSubmission({
          audio_id: selectedNoteForEditing?.id,
          title: value,
          note_type: recordingDetail.is_template ? undefined : recordingDetail?.note_type?.toString(),
          template_id: !recordingDetail.is_template ? undefined : recordingDetail?.template_id,
          category_type: recordingDetail.category_type,
          modality_mode: recordingDetail.modality_type,
          gender_type: recordingDetail.gender_type,
          location_type: recordingDetail.location_type,
          duration_type: recordingDetail.duration_type,
          duration_addon_type: recordingDetail.duration_addon_type,
          client_id: recordingDetail.client_id,
        });
        // Edit & Regenerate does NOT rewrite the note in place: write_note
        // mints a NEW AudioFile (edited_from_note = the one being edited) and
        // returns it. The re-offer therefore has to wait on the id in the
        // RESPONSE — the edited note's own id never goes ready again, so
        // keying off it meant the re-offer never fired at all.
        const regeneratedNote = response?.data as { id?: number; client?: number } | undefined;
        if (regeneratedNote?.id) {
          eventEmitter.emit("noteRegenerated", {
            id: regeneratedNote.id,
            previousId: selectedNoteForEditing.id,
            client: regeneratedNote.client ?? recordingDetail.client_id,
          });
        }
        // Regeneration is async and the drawer closes straight away — without
        // this, an unchanged submit looks like the button did nothing.
        message.success("Regenerating note…");
        if (clientUuid) {
          navigate(`${rootPath}/clients/${clientUuid}`);
        } else {
          navigate(`${rootPath}`);
        }
        dispatch(setCreateModalState(false));
        dispatch(setNotesViewVisible(false));
        dispatch(setCustomiseSectionState(false));
        resetRecordingState();
      } catch (error) {
        console.error("Error updating note:", error);
        Swal.fire({
          icon: "error",
          title: "Note submission failed.",
          text: "Please try again.",
        });
      } finally {
        setUpdatingNote(false);
      }
    };

    const showErrorConnectionSwal = () => {
      Swal.fire({
        icon: "error",
        title: "Network issues detected in connection",
        text: "We are detecting network issues in connecting to our servers. Please try again by refreshing your browser.",
      });
    };

    const handleHeadphonesToggle = (checked: boolean) => {
      dispatch(setWithHeadphones(checked));
    };

    /**
     * Validates that audio data is being captured at 15 seconds
     * Checks both GCS upload and IndexedDB backup to detect capture failures early
     *
     * IMPORTANT: Only triggers if we can SUCCESSFULLY verify both sources are empty.
     * If there's an error accessing either source, we don't treat it as "0 bytes" to avoid false negatives.
     * If IndexedDB backup is disabled, we only validate GCS uploads.
     */
    const validateRecordingDataCapture = async () => {
      const audioId = captureRecordingIdRef.current;
      if (!audioId) {
        console.warn("[VALIDATION] No audioId found for validation check");
        return;
      }

      const isIndexedDBEnabled = USE_INDEXEDDB_BACKUP;
      let gcsCheckSuccessful = false;
      let gcsUploadedBytes = -1; // -1 = unable to check
      let indexedDBCheckSuccessful = false;
      let indexedDBSize = -1; // -1 = unable to check
      let indexedDBStats = null;

      try {
        // Try to check GCS uploaded bytes
        try {
          const debugInfo = resumableUploadManager.getDebugInfo() as { uploadedBytes: number };
          if (debugInfo && typeof debugInfo.uploadedBytes === "number") {
            gcsUploadedBytes = debugInfo.uploadedBytes;
            gcsCheckSuccessful = true;
          }
        } catch (gcsError) {
          console.warn("[VALIDATION] Failed to get GCS upload info:", gcsError);
        }

        // Try to check IndexedDB buffer stats (only if enabled)
        if (isIndexedDBEnabled) {
          try {
            indexedDBStats = await getChunkStats(audioId.toString());
            // Only consider it successful if we got valid stats back
            // Note: getChunkStats returns { count, totalBytes } not totalSize
            if (indexedDBStats && typeof indexedDBStats.totalBytes === "number") {
              indexedDBSize = indexedDBStats.totalBytes as number;
              indexedDBCheckSuccessful = true;
            }
          } catch (indexedDBError) {
            console.warn("[VALIDATION] Failed to get IndexedDB stats:", indexedDBError);
          }
        } else {
          // IndexedDB backup is disabled - skip validation for it
          console.log("[VALIDATION] IndexedDB backup disabled, skipping IndexedDB validation");
          indexedDBCheckSuccessful = true; // Consider it "successful" (N/A)
          indexedDBSize = -999; // Special value indicating "disabled/not applicable"
        }

        console.log(
          `[VALIDATION] ${VALIDATION_TIME_SECONDS}s check - audioId: ${audioId}, IndexedDB enabled: ${isIndexedDBEnabled}, GCS bytes: ${gcsUploadedBytes} (verified: ${gcsCheckSuccessful}), IndexedDB size: ${indexedDBSize} (verified: ${indexedDBCheckSuccessful})`,
        );

        // CRITICAL: Determine if we have a recording failure
        // If IndexedDB is disabled: Only check GCS (must be 0 and verified)
        // If IndexedDB is enabled: Both must be 0 and verified
        const hasRecordingFailure = isIndexedDBEnabled
          ? gcsCheckSuccessful && indexedDBCheckSuccessful && gcsUploadedBytes === 0 && indexedDBSize === 0
          : gcsCheckSuccessful && gcsUploadedBytes === 0;

        if (hasRecordingFailure) {
          const errorContext = {
            audioId,
            indexedDBBackupEnabled: isIndexedDBEnabled,
            indexedDBSize,
            indexedDBCheckSuccessful,
            indexedDBStats,
            mediaRecorderState: mediaRecorder?.state,
            mediaRecorderMimeType: mediaRecorder?.mimeType,
            streamActive: mediaRecorder?.stream?.active,
            audioTracksCount: mediaRecorder?.stream?.getAudioTracks()?.length || 0,
            recordingDevice: {
              deviceId: recordingDevice?.deviceId,
              groupId: recordingDevice?.groupId,
              label: recordingDevice?.label,
            },
            ...resumableUploadManager.getDebugInfo(),
            withHeadphones,
            systemMetadata: getSystemMetadata(),
          };

          console.error(
            `[VALIDATION] CRITICAL: No data captured after ${VALIDATION_TIME_SECONDS} seconds (verified sources show 0 bytes)`,
            errorContext,
          );

          Sentry.captureException(
            new Error(`Recording validation failed: No audio data captured after ${VALIDATION_TIME_SECONDS} seconds`),
            {
              extra: errorContext,
              level: "error",
              tags: {
                validation_type: `${VALIDATION_TIME_SECONDS}s_recording_check`,
                has_gcs_data: "false",
                has_indexeddb_data: isIndexedDBEnabled ? "false" : "disabled",
                gcs_check_successful: "true",
                indexeddb_check_successful: indexedDBCheckSuccessful ? "true" : "false",
                indexeddb_backup_enabled: isIndexedDBEnabled ? "true" : "false",
              },
            },
          );

          // TODO: Uncomment after confirming this is a real issue in Sentry
          Swal.fire({
            icon: "error",
            title: "Recording Issue Detected",
            text: "We're unable to capture audio from your session. This is a safety check to make sure we can capture audio. Please check your microphone settings on the drop down menu and try again. Or refresh the browser and try again.",
          });
          await handleCancel();
        } else if (!gcsCheckSuccessful || (isIndexedDBEnabled && !indexedDBCheckSuccessful)) {
          // Log when we couldn't verify required sources (for debugging, but not a critical error)
          console.log(
            `[VALIDATION] Could not fully verify recording state - GCS check: ${gcsCheckSuccessful}, IndexedDB check: ${indexedDBCheckSuccessful}, IndexedDB enabled: ${isIndexedDBEnabled}`,
          );

          // Still log to Sentry but as info level to track access issues
          Sentry.captureMessage("Recording validation check incomplete - unable to verify all sources", {
            level: "info",
            extra: {
              audioId,
              indexedDBBackupEnabled: isIndexedDBEnabled,
              gcsUploadedBytes,
              gcsCheckSuccessful,
              indexedDBSize,
              indexedDBCheckSuccessful,
            },
            tags: {
              validation_type: "15s_recording_check_incomplete",
            },
          });
        }
      } catch (error) {
        console.error("[VALIDATION] Unexpected error during recording validation:", error);
        Sentry.captureException(error, {
          extra: {
            audioId,
            validationStep: "15s_recording_check",
            indexedDBBackupEnabled: isIndexedDBEnabled,
          },
        });
      }
    };

    const initiateMediaRecorder = async (withHeadphones = false, isDeviceChange = false) => {
      const config: MediaRecorderConfig = {
        recordingDevice,
        audioContextRef,
        destinationRef,
        currentAudioSourceRef,
        displayAudioSourceRef,
        checkMicrophoneMuteStatus,
        handleCancel,
        setStream,
        setRetryCount,
        dispatch,
        setRecordMimeType,
        setMediaRecorder,
        setRecordingDevice,
        audioBitsPerSecond: RECORDER_AUDIO_BITRATE_BPS_GCS,
        retryCount,
        captureRecordingIdRef, // Pass captureRecordingIdRef for GCS mute detection
      };

      return await initMediaRecorder(config, withHeadphones, isDeviceChange);
    };

    const handleStartRecording = async () => {
      const startRecordingConfig: StartRecordingConfig = {
        recorderTimesliceMs: RECORDER_TIMESLICE_MS_GCS,
        withHeadphones,
        captureRecordingIdRef,
        recordingDevice,
        getCaptureDetails,
        setRecordingTime,
        displayRecordingTimer,
        dispatch,
        setIsRecording,
        setIsPaused,
        initiateMediaRecorder,
      };

      await startRecording(startRecordingConfig);
    };

    // Resume recording function for restarting an existing session
    const handleResumeRecording = async () => {
      console.log("[RESUME] Starting resume recording flow");

      const resumeRecordingConfig: StartRecordingConfig = {
        recorderTimesliceMs: RECORDER_TIMESLICE_MS_GCS,
        withHeadphones,
        isResume: true,
        captureRecordingIdRef,
        recordingDevice,
        getCaptureDetails,
        setRecordingTime,
        displayRecordingTimer,
        dispatch,
        setIsRecording,
        setIsPaused,
        initiateMediaRecorder,
      };

      await startRecording(resumeRecordingConfig);
    };

    const handleNewDevice = async () => {
      try {
        // Before switching devices, try to flush any pending container segments
        // This helps prevent accumulation of incomplete WebM containers during device switches
        if (resumableUploadManager.hasActiveSession()) {
          console.log("Device switching detected, attempting to flush pending container segments");
          try {
            // Force upload of any pending container segments before device switch
            await resumableUploadManager.flushPendingChunks();
          } catch (error) {
            console.warn("Failed to flush pending container segments during device switch:", error);
            // Continue with device switch anyway
          }
        }

        const deviceChangeConfig: DeviceChangeConfig = {
          recordingDevice,
          audioContextRef,
          destinationRef,
          currentAudioSourceRef,
          mediaRecorder,
          withHeadphones,
          isPaused,
          recorderTimesliceMs: RECORDER_TIMESLICE_MS_GCS,
          checkMicrophoneMuteStatus,
          setStream,
          setRetryCount,
          dispatch,
          setRecordingDevice,
        };

        await handleDeviceChange(deviceChangeConfig);
      } catch (error) {
        console.error("Error in switching to new device: ", error);
      }
    };

    const handleUploadFile = async (client_id: number, client_uuid: string) => {
      const loading = message.loading("Uploading audio in background..", 0);
      const file = audioFileRef.current;
      const value = titleRef.current?.input?.value || "Untitled Session";
      if (!file) {
        message.error("No audio file selected");
        return;
      }

      let audioId: number | null = null;

      try {
        if (recordingDetail.note_type === undefined) {
          message.error("Please select a note type");
          return;
        }

        // Determine content type from file
        const contentType = file.type || "audio/mpeg";

        // Step 1: Initiate upload - create AudioFile record and get signed URL
        const initiateResponse = await initiateFileUpload({
          title: value,
          ...(recordingDetail.is_template && recordingDetail?.template_id
            ? { template_id: recordingDetail.template_id }
            : { note_type: recordingDetail.note_type }),
          location_type: recordingDetail.location_type,
          duration_type: recordingDetail.duration_type,
          duration_addon_type: recordingDetail.duration_addon_type,
          client_id: client_id,
          content_type: contentType,
          is_locked: isLastPlusOneNote(userInfo),
          system_metadata: getSystemMetadata(),
          file_name: file.name,
        });

        if (initiateResponse.status !== 201) {
          throw new Error("Failed to initiate upload");
        }

        const { audio_id, signed_url, content_type: serverContentType } = initiateResponse.data;
        audioId = audio_id;
        console.log("Upload initiated, audio_id:", audio_id);

        // Step 2: Upload file directly to GCS using signed URL
        const uploadSuccess = await uploadFileToGCS(signed_url, file, serverContentType);

        if (!uploadSuccess) {
          throw new Error("Failed to upload file to GCS");
        }

        console.log("File uploaded to GCS successfully");

        // Step 3: Notify backend that upload is complete to trigger transcription
        const completeResponse = await completeFileUpload(audio_id);
        loading();

        console.log("File Upload Complete Response:", completeResponse);
        if (completeResponse.status === 200) {
          message.success("Audio uploaded successfully. Processing in background..");
          trackEvent(EventType.UPLOAD_AUDIO_FINAL, {
            audioFileId: audio_id,
          });
          eventEmitter.emit("noteCreated", completeResponse.data);
          navigate(`${rootPath}/clients/${client_uuid}`);
          // resetRecordingState will handle clearing file uploads and Redux state
          resetRecordingState();
          // Show payment modal if needed
          if (showPaymentModal(userInfo)) {
            handleOpenPaymentModal();
          }
        } else {
          throw new Error("Failed to complete upload");
        }
      } catch (error) {
        loading();
        console.error("Upload Error:", error);

        // Cancel the created audio file if it exists to prevent dangling records
        if (audioId !== null) {
          try {
            await handleCancelRecording({ audio_id: String(audioId), status: "cancel" });
            console.log("Cancelled audio file due to upload failure, audio_id:", audioId);
          } catch (cancelError) {
            console.error("Failed to cancel audio file:", cancelError);
          }
        }

        Swal.fire({
          icon: "error",
          title: "Audio upload and processing failed.",
          text: "Please try again.",
        });
        resetRecordingState();
      }
    };

    const handleUploadPDFs = async (client_id: number, client_uuid: string) => {
      const loading = message.loading("Uploading PDFs in background..", 0);
      const value = titleRef.current?.input?.value || "Untitled Session";

      if (pendingPdfFiles.length === 0) {
        message.error("No PDF files selected");
        loading();
        return;
      }

      try {
        if (recordingDetail.note_type === undefined) {
          message.error("Please select a note type");
          loading();
          return;
        }

        const noteData = {
          title: value,
          note_type: recordingDetail.is_template ? undefined : recordingDetail.note_type,
          template_id: recordingDetail.is_template ? recordingDetail.template_id : undefined,
          category_type: recordingDetail.category_type,
          gender_type: recordingDetail.gender_type,
          language_type: recordingDetail.language_type,
          location_type: recordingDetail.location_type,
          duration_type: recordingDetail.duration_type,
          duration_addon_type: recordingDetail.duration_addon_type,
          client_id: client_id,
          is_locked: isLastPlusOneNote(userInfo),
          system_metadata: getSystemMetadata(),
        };

        const response = await uploadPDFsAndCreateNote(pendingPdfFiles, noteData);
        loading();

        if (response.status === 201) {
          message.success("PDFs uploaded successfully. Processing and generating note in background...", 5);
          trackEvent(EventType.UPLOAD_AUDIO_FINAL, {
            audioFileId: response?.data?.id,
          });
          eventEmitter.emit("noteCreated", response.data);
          navigate(`${rootPath}/clients/${client_uuid}`);
          resetRecordingState();
          setPendingPdfFiles([]);
          if (showPaymentModal(userInfo)) {
            handleOpenPaymentModal();
          }
        } else {
          throw new Error("PDF upload failed");
        }
      } catch (error) {
        loading();
        console.error("PDF Upload Error:", error);
        Swal.fire({
          icon: "error",
          title: "PDF upload and processing failed.",
          text: "Please try again.",
        });
        resetRecordingState();
      }
    };

    const handleDescribeSession = async (client_id: number, client_uuid: string) => {
      const title = titleRef.current?.input?.value || "Untitled Session";
      const description = notesRef.current?.resizableTextArea?.textArea.value || "";
      try {
        if (recordingDetail.note_type === undefined) {
          message.error("Please select a note type");
          return;
        }
        const body = {
          note_text: description,
          title: title,
          note_type: recordingDetail.is_template ? undefined : recordingDetail?.note_type?.toString(),
          template_id: !recordingDetail.is_template ? undefined : recordingDetail?.template_id,
          category_type: recordingDetail?.category_type?.toString(),
          modality_mode: recordingDetail.modality_type,
          gender_type: recordingDetail?.gender_type?.toString(),
          language_type: recordingDetail.language_type,
          location_type: recordingDetail.location_type,
          duration_type: recordingDetail.duration_type,
          duration_addon_type: recordingDetail.duration_addon_type,
          client_id: client_id,
          is_locked: isLastPlusOneNote(userInfo),
        };
        const shouldAttachPdfs = isCustomTemplate(recordingDetail) && contextPdfFiles.length > 0;
        const response = shouldAttachPdfs
          ? await handleWriteNoteSubmissionWithContext(body, contextPdfFiles)
          : await handleWriteNoteSubmission(body);

        trackEvent(EventType.WRITE_AUDIO_FINAL, {
          audioFileId: response?.data?.id,
        });
        eventEmitter.emit("noteCreated", response.data);
        setSelectedClient(clientsById[client_id] ?? { id: client_id, uuid: client_uuid });
        navigate(`${rootPath}/clients/${client_uuid}`);
        resetRecordingState();

        // Show payment modal if needed
        if (showPaymentModal(userInfo)) {
          handleOpenPaymentModal();
        }
      } catch (error) {
        console.error("Error submitting note:", error);
        Swal.fire({
          icon: "error",
          title: "Note submission failed.",
          text: "Please try again.",
        });
        resetRecordingState();
      }
    };

    const handleSubmitClient = async () => {
      const clientNameValidationResult = validateClientName(clientForm, referIndividualBy);

      if (clientNameValidationResult) {
        message.warning(clientNameValidationResult);
        return;
      }

      return createClient({
        ...clientForm,
        name: clientForm.name.trim(),
        diagnosis_ids: clientForm.diagnosis_ids,
      })
        .then((res) => {
          message.success(`${referIndividualBy} added successfully`);
          // Optimistically insert the new client (patches byId + sidebar) — no full-list refetch.
          dispatch(addClient(res.data));
          return res.data;
        })
        .catch((error) => {
          console.error(error);
          message.error(`Failed to create ${referIndividualBy.toLowerCase()}`);
        });
    };

    const handleClientSelection = async () => {
      if (addClientView) {
        const newClient = await handleSubmitClient();
        if (!newClient) {
          message.error(`Failed to create ${referIndividualBy.toLowerCase()}`);
          return [null, null];
        }
        return [newClient.id, newClient.uuid];
      }

      const client_id = recordingDetail.client_id;
      const clientUuid = recordingDetail.client_uuid ?? (client_id != null ? clientsById[client_id]?.uuid : undefined);

      if (!client_id && !addClientView) {
        message.error(`Please select a ${referIndividualBy.toLowerCase()} or choose to add a new one`);
        return [null, null];
      }

      return [client_id, clientUuid];
    };

    const handleNoneSession = async (client_id: number, client_uuid: string) => {
      const title = titleRef.current?.input?.value || "Untitled Session";
      try {
        if (recordingDetail.note_type === undefined) {
          message.error("Please select a note type");
          return;
        }
        const response = await handleWriteNoteSubmission({
          note_text: "",
          title: title,
          note_type: recordingDetail.is_template ? undefined : recordingDetail?.note_type?.toString(),
          template_id: !recordingDetail.is_template ? undefined : recordingDetail?.template_id,
          category_type: recordingDetail?.category_type?.toString(),
          modality_mode: recordingDetail.modality_type,
          gender_type: recordingDetail?.gender_type?.toString(),
          language_type: recordingDetail.language_type,
          location_type: recordingDetail.location_type,
          duration_type: recordingDetail.duration_type,
          duration_addon_type: recordingDetail.duration_addon_type,
          client_id: client_id,
          is_locked: isLastPlusOneNote(userInfo),
        });

        trackEvent(EventType.NONE_SESSION);
        eventEmitter.emit("noteCreated", response.data);
        setSelectedClient(clientsById[client_id] ?? { id: client_id, uuid: client_uuid });
        navigate(`${rootPath}/clients/${client_uuid}`);
        resetRecordingState();

        // Show payment modal if needed
        if (showPaymentModal(userInfo)) {
          handleOpenPaymentModal();
        }
      } catch (error) {
        console.error("Error submitting note:", error);
        Swal.fire({
          icon: "error",
          title: "Note submission failed.",
          text: "Please try again.",
        });
        resetRecordingState();
      }
    };

    const handleMainAction = async () => {
      if (checkDemoPath(true)) {
        trackEvent(EventType.CLICKED_ON_SUBMIT_NOTE_BEFORE_SIGNUP, {
          modalityMode: recordingDetail.modality_type,
        });
        return;
      }

      if (!validateFields()) {
        return;
      }

      if (startingSession) {
        return;
      }

      setStartingSession(true);
      dispatch(setAudioDevices(await fetchAudioInputDevices()));

      const result = await handleClientSelection();
      if (!result || (result[0] === null && result[1] === null)) {
        setStartingSession(false);
        return;
      }

      const [client_id, clientUuid] = result;

      if (!clientUuid) {
        console.error("Client UUID not found");
        message.error(`${referIndividualBy} not found. Please try again.`);
        setStartingSession(false);
        return;
      }

      const title = titleRef?.current?.input?.value || "Untitled Note";
      const currentRecordingDetail = {
        ...recordingDetail,
        title,
        client_id,
      };
      dispatch(setRecordingDetail(currentRecordingDetail));

      try {
        switch (recordingDetail.modality_type) {
          case "dictate":
          case "audio": {
            if (currentRecordingDetail.note_type === undefined) {
              message.error("Please select a note type");
              setStartingSession(false);
              return;
            }

            const isLocked = isLastPlusOneNote(userInfo);
            const payload = {
              title: currentRecordingDetail.title,
              note_type: recordingDetail.is_template ? undefined : recordingDetail?.note_type?.toString(),
              template_id: !recordingDetail.is_template ? undefined : recordingDetail?.template_id,
              category_type: currentRecordingDetail.category_type?.toString(),
              modality_mode: currentRecordingDetail.modality_type,
              gender_type: currentRecordingDetail.gender_type?.toString(),
              language_type: currentRecordingDetail.language_type.toString(),
              location_type: currentRecordingDetail.location_type,
              duration_type: currentRecordingDetail.duration_type,
              duration_addon_type: currentRecordingDetail.duration_addon_type,
              client_id: currentRecordingDetail.client_id,
              is_capture: true,
              system_metadata: {
                ...getSystemMetadata(),
                with_headphones: withHeadphones,
                from_gcs: true,
              },
              is_locked: isLocked,
              content_type: recordMimeType || "audio/webm",
            };

            try {
              const audioId = await resumableUploadManager.initiate(payload);
              captureRecordingIdRef.current = audioId.toString();

              // Upload context PDFs right after getting audioId, before recording starts.
              // Extraction is synchronous (pypdf), so pdf_context is ready long before
              // transcription completes — no race condition.
              if (isCustomTemplate(recordingDetail) && contextPdfFiles.length > 0) {
                try {
                  await uploadSessionContextPdfs(audioId, contextPdfFiles);
                } catch (pdfError) {
                  console.error("Failed to upload context PDFs:", pdfError);
                  message.warning("PDF context upload failed. Note will generate without PDF context.");
                }
              }

              dispatch(
                setRecordingDetail({
                  ...currentRecordingDetail,
                  current_recording_id: audioId,
                }),
              );

              eventEmitter.emit("refreshNotes");
              eventEmitter.emit("noteCreated", {
                id: audioId,
                type: currentRecordingDetail.note_type,
              });

              dispatch(setUploadType(0));
              dispatch(setCreateModalState(false));
              setStartingSession(false);
              dispatch(setShowRecordingView(true));
              await handleStartRecording();
            } catch (initError) {
              console.error("Error initiating resumable upload:", initError);
              Sentry.captureException(initError);
              Swal.fire({
                icon: "error",
                title: "Failed to Start Session",
                text: "Could not initiate the recording session. Please check your connection and try again.",
              });
              resetRecordingState();
              setStartingSession(false);
              return;
            }
            break;
          }
          case "upload": {
            const hasPDFs = pendingPdfFiles.length > 0;
            const hasAudio = audioFileRef.current !== null;

            if (hasPDFs && !hasAudio) {
              await handleUploadPDFs(client_id, clientUuid);
            } else if (hasAudio) {
              await handleUploadFile(client_id, clientUuid);
            } else {
              message.error("Please select audio or PDF files to upload");
              return;
            }
            resetState();
            break;
          }
          case "write":
            await handleDescribeSession(client_id, clientUuid);
            resetState();
            break;
          case "none":
            await handleNoneSession(client_id, clientUuid);
            break;
          default:
            throw new Error("Invalid modality type");
        }

        // Navigate to the client page after started the note creation process
        navigate(`${rootPath}/clients/${clientUuid}`);
      } catch (error) {
        Sentry.captureException(error, {
          data: {
            title: title,
            client_id: client_id,
            note_type: recordingDetail?.note_type?.toString(),
            modality: recordingDetail.modality_type,
          },
        });
        resetRecordingState();
        showErrorConnectionSwal();
      } finally {
        setStartingSession(false);
      }
    };

    const setModalityType = (modality_type: string) => {
      dispatch(
        setRecordingDetail({
          ...recordingDetail,
          modality_type,
        }),
      );
    };

    const saveDetailsForRetrieval = () => {
      const title = titleRef?.current?.input?.value || "";
      const description = notesRef.current?.resizableTextArea?.textArea.value || "";
      dispatch(
        setDemoState({
          ...demoState,
          clientForm,
          recordingDetails: {
            ...recordingDetail,
            title,
            clientId: recordingDetail.client_id,
            description: description,
          },
          isNoteModalOpen: true,
        }),
      );
    };

    const checkDemoPath = (isSubmit = false) => {
      if (isDemoPath) {
        if (isSubmit) {
          saveDetailsForRetrieval();
        }
        dispatch(setCreateModalState(false));
        dispatch(setShowDemoSignupModal(true));
      }
      return isDemoPath;
    };

    const scrollToBottom = () => {
      const scrollSection = document.getElementsByClassName("ant-drawer-body")[0];
      if (scrollSection) {
        scrollSection.scrollTo({
          top: scrollSection.scrollHeight,
          behavior: "smooth",
        });
      }
    };

    const resetState = () => {
      hasRunValidationRef.current = false; // Reset validation flag
      // resetRecordingState will handle clearing file uploads and Redux state
      resetRecordingState(true);
      dispatch(setCreateModalState(false));
      dispatch(resetClientForm());
      dispatch(setAddClientView(false));
      setStartingSession(false);
      clearFileUploads();
    };

    const handleDrawerClose = () => {
      if (startingSession) {
        console.warn("Closed drawer during session initiation.");
        return;
      }
      if (resumableUploadManager.hasActiveSession()) {
        console.warn("Closing drawer with an active upload session. Session might be orphaned.");
      }
      resetState();
    };

    const navigateToTemplates = () => {
      if (isMobilePath) {
        dispatch(setShowRestrictionModal(true));
      } else {
        dispatch(setCreateModalState(false));
        dispatch(setSelectedView(ViewType.TEMPLATE));
        navigate(`/templates`);
      }
    };

    const renderNoteTypes = () => {
      if (isFromGWUL(userInfo)) {
        return <NoteTypeSelector noteTypes={GWUL_NOTE_TYPES} />;
      }

      if (isFromLLT(userInfo)) {
        return <NoteTypeSelector noteTypes={LLT_NOTE_TYPES} />;
      }

      return <NoteTypeSelector noteTypes={DEFAULT_NOTE_TYPES} />;
    };

    return (
      <Drawer
        destroyOnClose
        title={isEditNote ? "Edit Document" : "Create Document"}
        width={700}
        height={isMobile && !isMobilePath ? "80%" : "100%"}
        placement={isMobile ? "bottom" : "right"}
        open={isCreateModalVisible}
        onClose={handleDrawerClose}
        footer={
          isEditNote
            ? [
                <Button
                  key="update-note"
                  type="primary"
                  disabled={updatingNote || (!isEditNote && !recordingDetail.title && !isCaptureEnabled)}
                  loading={updatingNote}
                  className={styles[`${componentName}__modal-button-primary`]}
                  onClick={handleNoteUpdate}
                >
                  Update and Regenerate Note
                </Button>,
              ]
            : [
                <Button
                  key="create-note"
                  loading={startingSession}
                  disabled={startingSession}
                  type="primary"
                  className={styles[`${componentName}__modal-button-primary`]}
                  onClick={handleMainAction}
                >
                  {recordingDetail?.modality_type === "write" && "Generate Note"}
                  {recordingDetail?.modality_type === "upload" && "Generate Note"}
                  {recordingDetail?.modality_type === "dictate" && "Start Dictation"}
                  {recordingDetail?.modality_type === "audio" && "Start Session"}
                  {recordingDetail?.modality_type === "none" && "Generate"}
                </Button>,
              ]
        }
      >
        <Row gutter={isMobile ? [16, 16] : [12, 12]}>
          <Col span={24}>
            <div className={styles[`${componentName}__modal-row-header`]} style={{ marginTop: 0 }}>
              <Typography>CHOOSE DOCUMENT TYPE</Typography>
            </div>
          </Col>
          {renderNoteTypes()}
          {
            <Col span={24} className={styles[`${componentName}__modal-row-header-sub`]} onClick={navigateToTemplates}>
              <PlusOutlined /> See More Templates from Gallery{" "}
              <Badge
                count={
                  <Flex className={styles[`${componentName}__modal-row-header-sub-badge`]}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M2.99922 1.19922C3.33059 1.19922 3.59922 1.46785 3.59922 1.79922V2.39922H4.19922C4.53059 2.39922 4.79922 2.66785 4.79922 2.99922C4.79922 3.33059 4.53059 3.59922 4.19922 3.59922H3.59922V4.19922C3.59922 4.53059 3.33059 4.79922 2.99922 4.79922C2.66785 4.79922 2.39922 4.53059 2.39922 4.19922V3.59922H1.79922C1.46785 3.59922 1.19922 3.33059 1.19922 2.99922C1.19922 2.66785 1.46785 2.39922 1.79922 2.39922H2.39922V1.79922C2.39922 1.46785 2.66785 1.19922 2.99922 1.19922ZM2.99922 7.19922C3.33059 7.19922 3.59922 7.46785 3.59922 7.79922V8.39922H4.19922C4.53059 8.39922 4.79922 8.66785 4.79922 8.99922C4.79922 9.33059 4.53059 9.59922 4.19922 9.59922H3.59922V10.1992C3.59922 10.5306 3.33059 10.7992 2.99922 10.7992C2.66785 10.7992 2.39922 10.5306 2.39922 10.1992V9.59922H1.79922C1.46785 9.59922 1.19922 9.33059 1.19922 8.99922C1.19922 8.66785 1.46785 8.39922 1.79922 8.39922H2.39922V7.79922C2.39922 7.46785 2.66785 7.19922 2.99922 7.19922Z"
                        fill="white"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M7.19919 1.19922C7.47143 1.19922 7.70955 1.38251 7.77921 1.64568L8.48674 4.31858L10.499 5.47951C10.6848 5.58666 10.7992 5.78479 10.7992 5.99922C10.7992 6.21365 10.6848 6.41178 10.499 6.51893L8.48674 7.67986L7.77921 10.3528C7.70955 10.6159 7.47143 10.7992 7.19919 10.7992C6.92695 10.7992 6.68883 10.6159 6.61916 10.3528L5.91163 7.67986L3.89938 6.51893C3.71365 6.41177 3.59922 6.21365 3.59922 5.99922C3.59922 5.78479 3.71365 5.58666 3.89938 5.47951L5.91163 4.31858L6.61916 1.64568C6.68883 1.38251 6.92695 1.19922 7.19919 1.19922Z"
                        fill="white"
                      />
                    </svg>
                    New
                  </Flex>
                }
                style={{ backgroundColor: "var(--primary)" }}
              />
            </Col>
          }
          {!isEditNote && (
            <>
              <RowHeader title="CHOOSE MODE" />
              <Col span={24}>
                <Flex wrap="wrap" gap={"5px 0px"}>
                  {MODALITY_LIST.map((modality) => (
                    <SelectableBadge
                      key={modality.type}
                      count={modality.title}
                      leftIcon={modality.icon}
                      disabled={recordingDetail.note_type === NoteType.CONSULT_NOTE && modality.type !== "write"}
                      selected={recordingDetail.modality_type === modality.type}
                      onClick={() => {
                        setModalityType(modality.type);
                      }}
                    />
                  ))}
                </Flex>
                <Flex style={{ marginTop: 8 }}>
                  {recordingDetail.modality_type === ModalityType.LIVE_SESSION && (
                    <span className={`${styles[`${componentName}__modal-row-subtext`]}`}>
                      Capture your session in real-time as you work with {referIndividualBy}s.
                    </span>
                  )}
                  {recordingDetail.modality_type === ModalityType.DICTATE && (
                    <span className={`${styles[`${componentName}__modal-row-subtext`]}`}>
                      Verbally describe your session.
                    </span>
                  )}
                  {recordingDetail.modality_type === ModalityType.UPLOAD && (
                    <span className={`${styles[`${componentName}__modal-row-subtext`]}`}>
                      Import existing audio recordings to create session notes.
                    </span>
                  )}
                  {recordingDetail.modality_type === ModalityType.WRITE && (
                    <span className={`${styles[`${componentName}__modal-row-subtext`]}`}>
                      Type a summary of your session and {referIndividualBy.toLowerCase()} observations.
                    </span>
                  )}
                  {recordingDetail.modality_type === ModalityType.NONE && (
                    <span className={`${styles[`${componentName}__modal-row-subtext`]}`}>
                      Use {referIndividualBy.toLowerCase()} history to create one-offs like Treatment Plans, Homeworks,
                      and more. Document created is based off of {referIndividualBy.toLowerCase()} history.
                    </span>
                  )}
                </Flex>
              </Col>
            </>
          )}
          <RowHeader title={`CHOOSE ${referIndividualBy.toUpperCase()}`} />
          {!addClientView ? (
            <>
              <ClientSelector />
              <EncounterPatientBar />
            </>
          ) : (
            <Col span={24} className={`${styles[`${componentName}__modal-row`]}`}>
              <Card
                title={`Add ${referIndividualBy}`}
                id="special-card"
                extra={
                  <CloseOutlined
                    onClick={() => {
                      dispatch(setAddClientView(false));
                    }}
                  />
                }
              >
                <ClientForm />
              </Card>
            </Col>
          )}
          {shouldShowHeadphoneSwitch ? (
            <Col span={24} className={`${styles[`${componentName}__modal-row`]}`}>
              <div className={styles[`${componentName}__modal-row-header`]}>
                <Typography>USING HEADPHONES</Typography>
              </div>
              <Flex align="center" style={{ marginTop: 8 }} gap={8}>
                <Typography.Text>No</Typography.Text>
                <Switch checked={withHeadphones} onChange={handleHeadphonesToggle} />
                <Typography.Text>Yes</Typography.Text>
              </Flex>
              <a
                href="https://getnudgeai.com/headphones-faq"
                target="_blank"
                style={{
                  marginTop: 8,
                  fontSize: 14,
                }}
                rel="noreferrer"
              >
                How to? (supported browsers: Chrome, Microsoft Edge)
              </a>
            </Col>
          ) : null}
          <RowHeader title="ADD SESSION DETAILS" />
          <Col span={24} className={`${styles[`${componentName}__modal-row`]}`}>
            <Typography.Text>Title: (optional)</Typography.Text>
            <Input
              defaultValue={recordingDetail.title}
              ref={titleRef}
              maxLength={100}
              className={`${styles[`${componentName}__modal-row-field`]}`}
              placeholder="Document Title"
            />
          </Col>
          <LocationSelector />
          {userInfo?.practice_area !== PRACTICE_AREA.MEDICINE && <CPTCodeSelector />}
          {recordingDetail.modality_type === "write" && (
            <Col span={24} className={`${styles[`${componentName}__modal-row`]}`}>
              <Typography.Text>
                {recordingDetail.note_type === NoteType.CONSULT_NOTE
                  ? `Clinical Description`
                  : `Describe Session / Import old note:`}
              </Typography.Text>
              <TextArea
                defaultValue={recordingDetail.description}
                ref={notesRef}
                onFocus={scrollToBottom}
                autoSize={{ minRows: 12, maxRows: 15 }}
                placeholder={
                  recordingDetail.note_type === 13
                    ? "Enter your clinical description of the patient and reason for referral."
                    : "Describe your session with writing or copy paste an old note to generate a new clean note."
                }
                rows={4}
              />
            </Col>
          )}
          {recordingDetail.modality_type === "upload" && (
            <FileUploader
              audioFileRef={audioFileRef}
              pendingPdfFiles={pendingPdfFiles}
              onPendingPdfFilesChange={setPendingPdfFiles}
              onUploadTypeChange={setCurrentUploadTab}
            />
          )}
          {isCustomTemplate(recordingDetail) &&
            (recordingDetail.modality_type === "audio" ||
              recordingDetail.modality_type === "dictate" ||
              recordingDetail.modality_type === "write") && (
              <PDFContextUploader contextPdfFiles={contextPdfFiles} onContextPdfFilesChange={setContextPdfFiles} />
            )}
        </Row>
      </Drawer>
    );
  },
);

export default CreateNoteModal;
